import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Tag, Table, Form, Input, InputNumber, Button, Space, message, Typography, Alert,
} from 'antd';
import { ApiOutlined, SendOutlined, WifiOutlined } from '@ant-design/icons';
import { mqtt_api } from '../api/mqtt-api';

const SAMPLE = {
  pvPower: 3500,
  gridPower: -800,
  loadPower: 1800,
  batteryPower: 400,
  batterySoc: 82,
  batteryVoltage: 51.2,
  dailyEnergy: 12.4,
  totalEnergy: 880,
  dailyConsumed: 6.1,
  temperature: 41,
  status: 'normal',
};

export default function MqttLab() {
  const [status, setStatus] = useState({});
  const [messages, setMessages] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [sending, setSending] = useState(false);
  const [credLoading, setCredLoading] = useState(false);
  const [loopLoading, setLoopLoading] = useState(false);
  const [loopStatus, setLoopStatus] = useState({ running: false });
  const [revealed, setRevealed] = useState(null);
  const [form] = Form.useForm();
  const [credForm] = Form.useForm();

  const refresh = async () => {
    try {
      const [st, msg, creds, loop] = await Promise.all([
        mqtt_api.status(),
        mqtt_api.messages(),
        mqtt_api.credentials(),
        mqtt_api.simulateLoopStatus().catch(() => ({ success: false })),
      ]);
      if (st.success) setStatus(st.data || {});
      if (msg.success) setMessages(msg.data || []);
      if (creds.success) setCredentials(creds.data || []);
      if (loop.success) setLoopStatus(loop.data || { running: false });
    } catch (_error) {
      /* ignore poll errors */
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!loopStatus.running) {
      return undefined;
    }
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [loopStatus.running]);

  const toggleLoop = async (enabled) => {
    const deviceId = form.getFieldValue('deviceId') || 'HYA-18EB3DEC';
    try {
      setLoopLoading(true);
      const res = await mqtt_api.simulateLoop({
        enabled,
        deviceId,
        intervalMs: 10000,
        scenario: 'cycle',
      });
      if (res.success) {
        message.success(res.message);
        setLoopStatus(res.data || { running: enabled });
        await refresh();
      } else {
        message.error(res.message);
      }
    } catch (error) {
      message.error(error.message || 'Giả lập loop thất bại');
    } finally {
      setLoopLoading(false);
    }
  };

  const send = async (action, payload) => {
    const deviceId = form.getFieldValue('deviceId') || 'HYA-18EB3DEC';
    try {
      setSending(true);
      const res = await mqtt_api.simulate({ deviceId, action, payload });
      if (res.success) {
        message.success(res.message);
        await refresh();
      } else {
        message.error(res.message);
      }
    } catch (error) {
      message.error(error.message || 'Gửi thất bại');
    } finally {
      setSending(false);
    }
  };

  const sendFormData = async (values) => {
    const { deviceId, ...fields } = values;
    await send('data', fields);
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <h1 style={{ margin: 0 }}>Test MQTT · ESP32</h1>
        <Button icon={<ApiOutlined />} onClick={refresh}>
          Làm mới
        </Button>
      </Space>
      <Typography.Paragraph type="secondary">
        MQTTS chỉ nhận máy đã gán user và có <code>mqtt_credentials</code>.
        Password mặc định = HMAC firmware (tự ghi khi gán inverter). App BLE chỉ cần SSID/pass.
        Không poll tự động — bấm «Làm mới» khi cần.
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Broker"
              value={status.enabled ? 'ON' : 'OFF'}
              prefix={<WifiOutlined />}
              valueStyle={{ color: status.enabled ? '#3f8600' : '#cf1322' }}
            />
            <div style={{ marginTop: 8 }}>
              MQTTS <code>{status.host || 'giabinhtech.com'}:{status.tlsPort || 8883}</code>
              <Tag color={status.tlsEnabled ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                {status.tlsEnabled ? 'TLS' : 'NO TLS'}
              </Tag>
            </div>
            {!status.tlsEnabled && status.tlsError && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 12 }}
                message="MQTTS 8883 chưa listen — ESP32 không kết nối được"
                description={
                  <>
                    <div>{status.tlsError}</div>
                    {status.tlsHint && <div style={{ marginTop: 4 }}>{status.tlsHint}</div>}
                    {status.tlsCertPath && (
                      <div style={{ marginTop: 4 }}>
                        Cert: <code>{status.tlsCertPath}</code>
                      </div>
                    )}
                  </>
                }
              />
            )}
            <div style={{ marginTop: 4 }}>
              1883 {status.plainEnabled ? <Tag color="orange">mở</Tag> : <Tag>tắt</Tag>}
              HMAC <Tag>off</Tag>
              {' '}Cred {status.credentialAuth ? <Tag color="green">on</Tag> : <Tag>off</Tag>}
              {' '}User {status.userBindingRequired ? <Tag color="green">bound</Tag> : <Tag>off</Tag>}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Client đang nối" value={status.connectedClients || 0} prefix={<ApiOutlined />} />
            <div style={{ marginTop: 8 }}>
              {(status.clientIds || []).length
                ? status.clientIds.map((id) => <Tag key={id}>{id}</Tag>)
                : <Typography.Text type="secondary">Chưa có ESP32</Typography.Text>}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" title="Topic firmware">
            <div><code>{status.topicData || 'tosol/inverter/{deviceId}/data'}</code></div>
            <div style={{ marginTop: 8 }}><code>{status.topicStatus || 'tosol/inverter/{deviceId}/status'}</code></div>
          </Card>
        </Col>
      </Row>

      <Alert
        style={{ margin: '16px 0' }}
        type="info"
        showIcon
        message="ESP32 thật"
        description={
          <span>
            MQTTS 8883, clientId = username = deviceId (≤ 23 ký tự). Phải đăng ký thiết bị
            + gán user rồi copy mật khẩu vào BLE <code>mqttPass</code>. Topic chỉ
            <code>.../đúng-id/data|status</code>. LWT <code>offline</code> retain.
          </span>
        }
      />

      <Card title="Mật khẩu từng máy" style={{ marginBottom: 16 }}>
        <Form
          form={credForm}
          layout="inline"
          initialValues={{ deviceId: 'HYA-18EB3DEC' }}
          style={{ marginBottom: 12 }}
        >
          <Form.Item name="deviceId" rules={[{ required: true }, { max: 23 }]}>
            <Input placeholder="HYA-18EB3DEC" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item>
            <Space wrap>
              <Button
                type="primary"
                loading={credLoading}
                onClick={async () => {
                  const deviceId = credForm.getFieldValue('deviceId');
                  try {
                    setCredLoading(true);
                    const res = await mqtt_api.createCredential({ deviceId });
                    if (res.success) {
                      setRevealed(res.data);
                      message.success(res.message);
                      await refresh();
                    } else {
                      message.error(res.message);
                    }
                  } catch (error) {
                    message.error(error.message || 'Tạo mật khẩu thất bại');
                  } finally {
                    setCredLoading(false);
                  }
                }}
              >
                Tạo / xoay mật khẩu
              </Button>
            </Space>
          </Form.Item>
        </Form>
        {revealed?.password && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${revealed.deviceId} · ${revealed.auth || 'custom'}`}
            description={<code>{revealed.password}</code>}
          />
        )}
        <Table
          size="small"
          rowKey="deviceId"
          dataSource={credentials}
          pagination={false}
          columns={[
            { title: 'deviceId', dataIndex: 'deviceId' },
            { title: 'hint', dataIndex: 'hint', width: 80, render: (v) => (v ? `…${v}` : '—') },
            {
              title: '',
              width: 90,
              render: (_, row) => (
                <Button
                  size="small"
                  danger
                  onClick={async () => {
                    await mqtt_api.deleteCredential(row.deviceId);
                    message.success('Đã xóa — máy mất MQTTS');
                    await refresh();
                  }}
                >
                  Xóa
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card title="Giả lập ESP32 (web → server)">
            <Form
              form={form}
              layout="vertical"
              initialValues={{ deviceId: 'HYA-18EB3DEC', ...SAMPLE }}
              onFinish={sendFormData}
            >
              <Form.Item name="deviceId" label="deviceId / Client ID" rules={[{ required: true }, { max: 23 }]}>
                <Input placeholder="HYA-18EB3DEC" />
              </Form.Item>
              <Space style={{ marginBottom: 12 }} wrap>
                <Button onClick={() => send('status', 'online')}>Status online</Button>
                <Button onClick={() => send('status', 'offline')}>Status offline</Button>
                <Button onClick={() => {
                  form.setFieldsValue(SAMPLE);
                  form.submit();
                }}>
                  Gửi mẫu
                </Button>
                <Button
                  type={loopStatus.running ? 'default' : 'primary'}
                  danger={loopStatus.running}
                  loading={loopLoading}
                  onClick={() => toggleLoop(!loopStatus.running)}
                >
                  {loopStatus.running ? 'Dừng giả lập 10s' : 'Chạy giả lập 10s (PV/pin/tải)'}
                </Button>
              </Space>
              {loopStatus.running && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`Đang giả lập ${loopStatus.deviceId} · tick #${loopStatus.tick || 0} · SOC ${loopStatus.soc ?? '—'}% · ${loopStatus.scenario}`}
                  description="Chu kỳ 90s: (1) PV cao / Home thấp → sạc + xuất lưới · (2) lẫn lộn mạnh · (3) PV thấp / Home cao → xả + lấy lưới. SOC cũng nhảy rõ mỗi tick."
                />
              )}
              <Row gutter={8}>
                <Col span={12}><Form.Item name="pvPower" label="pvPower (W)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={12}><Form.Item name="loadPower" label="loadPower (W)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={12}><Form.Item name="gridPower" label="gridPower (W)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={12}><Form.Item name="batteryPower" label="batteryPower (W)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={12}><Form.Item name="batterySoc" label="batterySoc (%)"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={12}><Form.Item name="dailyEnergy" label="dailyEnergy (kWh)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={sending} block>
                Publish data
              </Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title="Log MQTT (ESP32 + giả lập)" extra={<Button size="small" onClick={refresh}>Làm mới</Button>}>
            <Table
              size="small"
              rowKey={(row, index) => `${row.at}-${index}`}
              dataSource={messages}
              pagination={{ pageSize: 12 }}
              scroll={{ x: 640 }}
              columns={[
                {
                  title: 'Giờ',
                  dataIndex: 'at',
                  width: 90,
                  render: (v) => (v ? new Date(v).toLocaleTimeString('vi-VN') : ''),
                },
                { title: 'deviceId', dataIndex: 'deviceId', width: 110 },
                {
                  title: 'action',
                  dataIndex: 'action',
                  width: 80,
                  render: (v) => <Tag color={v === 'status' ? 'orange' : 'green'}>{v}</Tag>,
                },
                {
                  title: 'payload',
                  dataIndex: 'payload',
                  ellipsis: true,
                  render: (v) => <code style={{ fontSize: 12 }}>{typeof v === 'string' ? v : JSON.stringify(v)}</code>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
